import { type OpportunityParticipant, type User } from "@entities";
import CancelIcon from "@mui/icons-material/Cancel";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import { IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { type ColumnProps, type HyperTableDef } from "@shared/frontend/hyper-table/types";
import { linko } from "@shared/linko";
import { useQueryClient } from "@tanstack/react-query";
import { adminParticipantActionErrorMessage } from "../../../helpers/admin-list-query";
import { useAlert } from "../../../contexts/AlertProvider";
import { invalidateEventParticipantQueries } from "../../../queries/invalidate-opportunities-table";
import PhoneNumberCell from "../PhoneNumberCell";
import UserNameAndPhoto from "../users-table/volunteers/components/UserNameAndPhoto";

export function useOpportunityInterestedColumns(
  hyperTable: HyperTableDef<OpportunityParticipant>,
  onChanged?: (approvedGroup?: boolean) => void,
): ColumnProps<OpportunityParticipant>[] {
  const { setAlertText, setSeverity } = useAlert();
  const queryClient = useQueryClient();

  const decide = async (
    participant: OpportunityParticipant,
    isApproved: boolean,
  ) => {
    try {
      await linko.opportunityParticipant.mutations.approveOrDeclineInterested({
        opportunityId: participant.opportunityId,
        associationId: participant.associationId,
        isApproved,
      });
      await Promise.all([
        hyperTable.context.current?.refetch("remove"),
        invalidateEventParticipantQueries(
          queryClient,
          participant.opportunityId,
        ),
      ]);
      onChanged?.(isApproved && !!participant.groupId);
      setAlertText(
        isApproved
          ? participant.groupId
            ? "הקבוצה אושרה והוקצתה למשימה"
            : "המתנדב נוסף למשימה"
          : participant.groupId
            ? "בקשת הקבוצה נדחתה"
            : "הבקשה נדחתה",
      );
      setSeverity("success");
    } catch (error) {
      setAlertText(adminParticipantActionErrorMessage(error));
      setSeverity("error");
    }
  };

  return [
    {
      field: "group.name",
      caption: "קבוצה",
      cell: ({ row }) => {
        const group = row.original.group;
        if (!group?.name) {
          return <Typography color="text.secondary">-</Typography>;
        }
        return (
          <Stack spacing={0.25}>
            <Typography fontWeight={600}>{group.name}</Typography>
            {group.size ? (
              <Typography variant="caption" color="text.secondary">
                {group.size} משתתפים
              </Typography>
            ) : null}
          </Stack>
        );
      },
    },
    {
      field: "association.userName",
      caption: "מוביל/ת הקבוצה",
      enableSorting: true,
      cell: ({ row }) => {
        return <UserNameAndPhoto user={row.original.association?.user as User} />;
      },
    },
    {
      field: "association.user.phoneNumber",
      caption: "טלפון",
      cell: ({ row }) => (
        <PhoneNumberCell phoneNumber={row.original.association?.user?.phoneNumber || ""} />
      ),
      preventRowClick: true,
    },
    { field: "association.user.numberID", caption: "מספר זהות" },
    { field: "association.user.email", caption: "דואר אלקטרוני" },
    {
      field: "association.user.skills.skill.name",
      caption: "כישורים",
      valueConverter: (row) => {
        return (
          row.association?.user?.skills
            ?.map((skill) => skill.skill?.name)
            ?.join(" ,") || "-"
        );
      },
    },
    {
      field: "association.user.gender",
      caption: "מגדר",
      valueConverter: (row) => {
        const gender = row.association?.user?.gender;
        if (gender === "MALE") return "זכר";
        if (gender === "FEMALE") return "נקבה";
        if (gender === "OTHER") return "אחר";
        return "-";
      },
    },
    {
      field: "createdAt",
      caption: "תאריך הרשמה",
      valueConverter: (row) => {
        return new Date(row.createdAt || "").toLocaleString("he");
      },
    },
    {
      field: "id",
      caption: "פעולות",
      preventRowClick: true,
      cell: ({ row }) => (
        <Stack direction="row" gap={0.5}>
          <Tooltip title="אישור">
            <IconButton
              size="small"
              color="success"
              aria-label="אישור בקשה"
              onClick={() => void decide(row.original, true)}
            >
              <PersonAddRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="דחייה">
            <IconButton
              size="small"
              color="error"
              aria-label="דחיית בקשה"
              onClick={() => void decide(row.original, false)}
            >
              <CancelIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];
}

export default useOpportunityInterestedColumns;
